import type { ProjectListResponse } from '@/entities/project/api/generated/model/projectListResponse';
import type { ProjectReferenceMatchDocumentRecordSchema } from '@/entities/project/api/generated/model/projectReferenceMatchDocumentRecordSchema';
import type { ProjectReferenceMatchDocumentRecordWithProjectsSchema } from '@/entities/project/api/generated/model/projectReferenceMatchDocumentRecordWithProjectsSchema';
import type { ProjectReferenceMatchMatchedProjectSchema } from '@/entities/project/api/generated/model/projectReferenceMatchMatchedProjectSchema';
import type {
  FundingSourceItem,
  Judgement,
  MatchedProject,
  MatchingCandidate,
} from '@/features/ai-matching-results/lib/types';
import { connectCodeAndName, formatCodeNamePairs } from '@/shared/lib/format';

/**
 * 参照マッチ表示APIのレスポンスを、AIマッチング結果ページの既存表示型へ変換するアダプタ群。
 *
 * 既存ウィジェット（FundingListTable / MatchedProjectsTable / サイドパネル）は mock 由来の
 * `FundingSourceItem` / `MatchedProject` 型を受け取る前提で組まれているため、ウィジェット側の型は
 * 変えず、API レスポンスをここで既存型に詰め替えて差し替える。
 */

/**
 * ユーザー選択状態（is_user_selected）から判定（judgement）を導出する。
 * - true  → matched（採用）
 * - false → not-matched（非採用）
 * - null / undefined → pending（未判定）
 *
 * API には judgement フィールドが無いため、この導出ロジックで表示用の判定値を生成する。
 */
export const deriveJudgement = (isUserSelected: boolean | null | undefined): Judgement => {
  if (isUserSelected === true) return 'matched';
  if (isUserSelected === false) return 'not-matched';
  return 'pending';
};

/**
 * 歳出科目・会計区分の整形に必要な構造的フィールド。
 * `ProjectReferenceMatchMatchedProjectSchema`（参照マッチ表示 API）と `ProjectListResponse`
 * （事業一覧 API）の双方が満たすため、どちらの事業表現でも整形ヘルパーを共有できる。
 */
type ExpenditureItemFields = {
  kan: { code: string; name: string };
  ko: { code: string; name: string };
  moku: { code: string; name: string };
  saimoku_code?: string;
  saimoku_name?: string;
  saisaimoku_code?: string;
  saisaimoku_name?: string;
};

type AccountingTypeFields = {
  accounting_type?: { code: string; name: string };
};

/** 歳出科目（款 / 項 / 目 / 細目 / 細々目）の表示文字列を生成する。 */
const formatExpenditureItem = (project: ExpenditureItemFields): string =>
  formatCodeNamePairs([
    [project.kan.code, project.kan.name],
    [project.ko.code, project.ko.name],
    [project.moku.code, project.moku.name],
    [project.saimoku_code, project.saimoku_name],
    [project.saisaimoku_code, project.saisaimoku_name],
  ]) ?? '';

/** 会計区分の表示文字列を生成する（未設定時は空文字）。 */
const formatAccountingType = (project: AccountingTypeFields): string => {
  if (!project.accounting_type) return '';
  return (
    connectCodeAndName({
      code: project.accounting_type.code,
      name: project.accounting_type.name,
    }) ?? ''
  );
};

/**
 * 事業一覧レスポンスの `reference_matches` から、指定財源 `(document_id, record_id)` に
 * マッチしたドキュメントレコードを取り出す。
 *
 * `GET /projects?reference_match_document_record_record_id=&reference_match_document_record_document_id=`
 * でサーバー側に対象事業を絞っても、各事業の `reference_matches[].documents[]` には selected な
 * document が全件含まれ、別 document_id の同一 record_id が混在しうる（BE は selected documents を
 * prefetch するため）。そのため **record_id だけでなく document_id も併用**して一意に特定し、
 * 判定（is_user_selected）・該当理由（record_citation_reason）・名称を正しい document から導出する。
 */
export const findMatchedDocument = ({
  project,
  documentId,
  recordId,
}: {
  project: ProjectListResponse;
  documentId: string;
  recordId: string;
}): ProjectReferenceMatchDocumentRecordSchema | undefined =>
  project.reference_matches
    ?.flatMap((match) => match.documents ?? [])
    .find((doc) => doc.document_id === documentId && doc.record_id === recordId);

/** マッチした事業（API） → 表示用 MatchedProject。 */
export const toMatchedProject = (
  project: ProjectReferenceMatchMatchedProjectSchema,
): MatchedProject => ({
  projectId: project.id,
  projectCode: project.project_code,
  projectName: project.project_name,
  judgement: deriveJudgement(project.is_user_selected),
  fiscalYear: project.year,
  expenditureItem: formatExpenditureItem(project),
  organization:
    connectCodeAndName({
      code: project.budget_management_division.code,
      name: project.budget_management_division.name,
    }) ?? '',
  budgetAmount: project.budget_amount,
  // 候補（マッチング判断理由つき）はサイドパネルで getReferenceMatch から取得するため一覧では空。
  matchingCandidates: [],
  accountingType: formatAccountingType(project),
  // 事業内容は表示APIに含まれないため空（事業詳細部分は別対応）。
  projectContent: '',
  tags: project.tags.map((tag) => ({ id: tag.id, name: tag.name })),
  source: project.source,
});

/**
 * 事業一覧レスポンス（`ProjectListResponse`） → 表示用 MatchedProject。
 *
 * `GET /projects?reference_match_document_record_record_id=` でサーバー側に絞り込んだ事業を、
 * 財源詳細ページの `MatchedProjectsTable` が受け取る既存の `MatchedProject` 型へ詰め替える。
 * 判定は当該財源 `(document_id, record_id)` にマッチした document の `is_user_selected` から導出する。
 */
export const toMatchedProjectFromListResponse = ({
  project,
  documentId,
  recordId,
}: {
  project: ProjectListResponse;
  documentId: string;
  recordId: string;
}): MatchedProject => {
  const matchedDocument = findMatchedDocument({ project, documentId, recordId });
  return {
    projectId: project.id,
    projectCode: project.project_code,
    projectName: project.project_name,
    judgement: deriveJudgement(matchedDocument?.is_user_selected),
    fiscalYear: project.year,
    expenditureItem: formatExpenditureItem(project),
    organization:
      connectCodeAndName({
        code: project.budget_management_division.code,
        name: project.budget_management_division.name,
      }) ?? '',
    budgetAmount: project.budget_amount,
    // 候補（マッチング判断理由つき）はサイドパネルで getReferenceMatch から取得するため一覧では空。
    matchingCandidates: [],
    accountingType: formatAccountingType(project),
    // 事業内容は事業一覧 API に含まれないため空（事業詳細部分は別対応）。
    projectContent: '',
    tags: (project.tags ?? []).map((tag) => ({ id: tag.id ?? '', name: tag.name ?? '' })),
    source: project.source,
  };
};

/** ドキュメントレコード（API） → 表示用 FundingSourceItem。 */
export const toFundingSourceItem = (
  record: ProjectReferenceMatchDocumentRecordWithProjectsSchema,
): FundingSourceItem => ({
  id: record.record_id,
  documentId: record.document_id,
  name: record.record_name ?? '',
  matchedProjects: record.projects.map(toMatchedProject),
});

/**
 * 事業参照マッチ取得（getReferenceMatch）の document レコード → サイドパネルのマッチング候補。
 *
 * `fundingSourceId` には `record_id` を入れ、パネルを開いた財源（record_id）と一致する候補を
 * ハイライト判定に使う。
 */
export const toMatchingCandidate = (
  doc: ProjectReferenceMatchDocumentRecordSchema,
): MatchingCandidate => ({
  candidateId: doc.id,
  title: doc.record_name,
  judgement: deriveJudgement(doc.is_user_selected),
  fundingSourceId: doc.record_id,
  matchReasons: doc.record_citation_reason ? [doc.record_citation_reason] : undefined,
  supplement: doc.record_description || undefined,
});
